import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { entitlementsApi } from '@/api/endpoints'
import { useApiMutation } from '@/api/hooks/useApiMutation'
import { queryKeys } from '@/api/queryKeys'
import type { Entitlement } from '@/api/types'

import { isAddonModule, type AddonModuleKey, type ModuleKey } from './module-keys'
import { getModule } from './registry'

/**
 * Stato di attivazione dei moduli, così come lo conosce il server.
 *
 * È il server a decidere: il client nasconde ciò che non è attivo per non far
 * perdere tempo, ma le rotte del backend rifiutano comunque le richieste. Un
 * modulo spento è una questione di contratto, non di interfaccia.
 */
export function useEntitlements() {
  return useQuery({
    queryKey: queryKeys.entitlements,
    queryFn: () => entitlementsApi.list(),
    staleTime: 5 * 60_000,
  })
}

/**
 * Se un modulo è utilizzabile adesso.
 *
 * I moduli di piattaforma sono sempre attivi. Mentre gli entitlement stanno
 * caricando, gli opzionali contano come spenti: è meglio mostrare in ritardo
 * una capacità che c'è, che mostrarne una che poi sparisce.
 */
export function useIsModuleActive(key: ModuleKey): boolean {
  const { data } = useEntitlements()

  if (!isAddonModule(key)) return true
  return data?.find((entitlement) => entitlement.module === key)?.active ?? false
}

export interface ModuleActivationState {
  entitlements: Entitlement[]
  isLoading: boolean
  isActive: (key: ModuleKey) => boolean
  setActive: (key: AddonModuleKey, active: boolean) => void
  /** Il modulo su cui è in corso un cambio di stato, se ce n'è uno. */
  pendingKey: AddonModuleKey | null
}

/**
 * Attiva e disattiva i moduli.
 *
 * L'aggiornamento è ottimistico: l'interruttore si muove subito, perché
 * aspettare mezzo secondo un interruttore fa sembrare rotta l'applicazione.
 * Se il server rifiuta, lo stato torna indietro e il messaggio dice perché.
 */
export function useModuleActivation(): ModuleActivationState {
  const queryClient = useQueryClient()
  const { data, isLoading } = useEntitlements()

  const mutation = useApiMutation({
    mutationFn: entitlementsApi.setActive,
    successMessage: ({ module, active }) =>
      active
        ? `Modulo ${getModule(module).name} attivato.`
        : `Modulo ${getModule(module).name} disattivato.`,

    onMutate: async ({ module, active }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.entitlements })
      const previous = queryClient.getQueryData<Entitlement[]>(queryKeys.entitlements)

      queryClient.setQueryData<Entitlement[]>(queryKeys.entitlements, (current) =>
        current?.map((entitlement) =>
          entitlement.module === module ? { ...entitlement, active } : entitlement,
        ),
      )

      return { previous }
    },

    onError: (_error, _variables, context) => {
      const previous = (context as { previous?: Entitlement[] } | undefined)?.previous
      if (previous) queryClient.setQueryData(queryKeys.entitlements, previous)
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.entitlements })
      // Spegnere un modulo può togliere dati dallo schermo: meglio ricaricare
      // tutto ciò che potrebbe dipenderne, invece di lasciare residui.
      void queryClient.invalidateQueries({ queryKey: queryKeys.recommendations })
    },
  })

  const entitlements = useMemo(() => data ?? [], [data])

  const isActive = useCallback(
    (key: ModuleKey) => {
      if (!isAddonModule(key)) return true
      return entitlements.find((entitlement) => entitlement.module === key)?.active ?? false
    },
    [entitlements],
  )

  const setActive = useCallback(
    (key: AddonModuleKey, active: boolean) => {
      mutation.mutate({ module: key, active })
    },
    [mutation],
  )

  return {
    entitlements,
    isLoading,
    isActive,
    setActive,
    pendingKey: mutation.isPending ? (mutation.variables?.module ?? null) : null,
  }
}
